import React, { useState } from 'react';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  LineChart, Line
} from 'recharts';
import { 
  ShoppingCart, DollarSign, Users, Store, Package,
  TrendingUp, TrendingDown, Eye, Download, Calendar,
  Star, Award, Crown, Sparkles, Zap, Rocket,
  MessageSquare, Bell, Settings, LogOut, User,
  CheckCircle, Clock, AlertCircle, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';

const salesData = [
  { name: 'Mon', sales: 4000, orders: 45 },
  { name: 'Tue', sales: 3000, orders: 38 },
  { name: 'Wed', sales: 5000, orders: 52 },
  { name: 'Thu', sales: 2780, orders: 28 },
  { name: 'Fri', sales: 6890, orders: 67 },
  { name: 'Sat', sales: 8390, orders: 89 },
  { name: 'Sun', sales: 7490, orders: 78 },
];

const categoryData = [
  { name: 'Indoor Plants', value: 35, color: '#10b981' },
  { name: 'Outdoor Plants', value: 25, color: '#3b82f6' },
  { name: 'Flowering Plants', value: 20, color: '#f59e0b' },
  { name: 'Succulents', value: 20, color: '#8b5cf6' },
];

const latestOrders = [
  { id: '#ORD-9912', product: 'Ceramic Pot Set', seller: 'Green Earth Nurseries', amount: 1200, status: 'Processing', customer: 'Rahul Sharma' },
  { id: '#ORD-9911', product: 'Monstera Large', seller: 'Paudhawala', amount: 499, status: 'Shipped', customer: 'Priya Patel' },
  { id: '#ORD-9910', product: 'Organic Fertilizer', seller: 'EcoLife Supplies', amount: 350, status: 'Delivered', customer: 'Amit Singh' },
  { id: '#ORD-9909', product: 'Snake Plant', seller: 'Green Thumb', amount: 299, status: 'Delivered', customer: 'Sneha Gupta' },
];

const topSellers = [
  { name: 'Green Earth Nurseries', revenue: 125000, growth: 15 },
  { name: 'Paudhawala', revenue: 98000, growth: 22 },
  { name: 'EcoLife Supplies', revenue: 67000, growth: 8 },
  { name: 'Flora Bliss', revenue: 54000, growth: 12 },
];

const Dashboard = () => {
  const [dateRange, setDateRange] = useState('week');
  
  const stats = [
    { label: 'Total Orders', value: '45.2K', change: '+12%', icon: ShoppingCart, color: '#3b82f6', bgColor: '#dbeafe' },
    { label: 'Total Revenue', value: '₹12.4M', change: '+23%', icon: DollarSign, color: '#10b981', bgColor: '#d1fae5' },
    { label: 'Total Users', value: '18.9K', change: '+18%', icon: Users, color: '#8b5cf6', bgColor: '#e9d5ff' },
    { label: 'Total Sellers', value: '245', change: '+5%', icon: Store, color: '#f59e0b', bgColor: '#fed7aa' },
    { label: 'Total Products', value: '3,842', change: '+8%', icon: Package, color: '#ef4444', bgColor: '#fee2e2' },
  ];

  const getStatusConfig = (status) => {
    switch(status) {
      case 'Delivered': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Shipped': return { icon: Clock, color: '#3b82f6', bg: '#dbeafe' };
      case 'Processing': return { icon: AlertCircle, color: '#f59e0b', bg: '#fed7aa' };
      default: return { icon: Clock, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const StatCard = ({ stat }) => {
    const Icon = stat.icon;
    return (
      <div className="stat-card">
        <div className="stat-icon" style={{ backgroundColor: stat.bgColor }}>
          <Icon size={22} color={stat.color} />
        </div>
        <div className="stat-info">
          <h3>{stat.value}</h3>
          <span>{stat.label}</span>
          <div className="stat-change positive">
            <TrendingUp size={12} />
            {stat.change}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="partner-dashboard-modern">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">Platform Overview</h1>
          <p className="page-subtitle">Welcome back! Here's what's happening on your platform</p>
        </div>
        <div className="header-actions">
          <div className="date-range">
            <Calendar size={16} />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <button className="btn-export">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, idx) => (
          <StatCard key={idx} stat={stat} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Sales Trend Chart */}
        <div className="chart-card large">
          <div className="chart-header">
            <div>
              <h3>Platform Sales Trend</h3>
              <p>Last 7 days performance</p>
            </div>
            <div className="chart-badge">
              <TrendingUp size={14} />
              <span>+14% Growth</span>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesData}>
                <defs>
                  <linearGradient id="colorSalesPartner" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                  formatter={(value) => [`₹${value}`, 'Sales']}
                />
                <Area type="monotone" dataKey="sales" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorSalesPartner)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Category Distribution</h3>
            <p>Sales by product category</p>
          </div>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height="200px">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="category-list">
            {categoryData.map((cat, idx) => (
              <div key={idx} className="category-item">
                <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                <span className="category-name">{cat.name}</span>
                <span className="category-value">{cat.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="two-column-grid">
        {/* Recent Orders */}
        <div className="data-card">
          <div className="card-header">
            <h3>Recent Platform Orders</h3>
            <Link to="/partner/orders" className="view-link">View All →</Link>
          </div>
          <div className="orders-list">
            {latestOrders.map((order) => {
              const statusConfig = getStatusConfig(order.status);
              const StatusIcon = statusConfig.icon;
              return (
                <div key={order.id} className="order-item">
                  <div className="order-info">
                    <div className="order-id">{order.id}</div>
                    <div className="order-product">{order.product}</div>
                    <div className="order-customer">{order.customer}</div>
                  </div>
                  <div className="order-details">
                    <div className="order-amount">₹{order.amount.toLocaleString()}</div>
                    <div className="order-seller">By {order.seller}</div>
                    <div className="order-status" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                      <StatusIcon size={12} />
                      {order.status}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Sellers */}
        <div className="data-card">
          <div className="card-header">
            <h3>Top Performing Sellers</h3>
            <Link to="/partner/sellers" className="view-link">View All →</Link>
          </div>
          <div className="top-sellers-list">
            {topSellers.map((seller, idx) => (
              <div key={idx} className="seller-item">
                <div className="seller-rank">#{idx + 1}</div>
                <div className="seller-info">
                  <div className="seller-name">{seller.name}</div>
                  <div className="seller-stats">
                    <span>💰 ₹{seller.revenue.toLocaleString()}</span>
                    <span>📈 {seller.growth}% growth</span>
                  </div>
                </div>
                <div className="seller-growth positive">
                  <TrendingUp size={14} />
                  +{seller.growth}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="insights-card">
        <div className="insights-header">
          <Sparkles size={20} />
          <h3>Key Insights</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-icon">📈</div>
            <div>
              <h4>Sales are up 23% this month</h4>
              <p>Outdoor plants category showing highest growth</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🌟</div>
            <div>
              <h4>Best performing seller</h4>
              <p>Green Earth Nurseries with ₹1.25L revenue</p>
            </div>
          </div>
          <div className="insight-item">
            <div className="insight-icon">🪴</div>
            <div>
              <h4>Most popular product</h4>
              <p>Monstera Deliciosa sold 1,245 units this month</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .partner-dashboard-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .date-range {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
        }

        .date-range select {
          border: none;
          background: transparent;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.3s ease;
          border: 1px solid #e8ecef;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .stat-icon {
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
        }

        .stat-info h3 {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 12px;
          color: #64748b;
          display: block;
        }

        .stat-change {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          margin-top: 6px;
        }

        .stat-change.positive {
          color: #10b981;
        }

        /* Charts Row */
        .charts-row {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
          margin-bottom: 28px;
        }

        .chart-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
        }

        .chart-card.large {
          grid-column: span 1;
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .chart-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .chart-header p {
          font-size: 12px;
          color: #94a3b8;
          margin: 2px 0 0;
        }

        .chart-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          background: #d1fae5;
          color: #10b981;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .chart-container {
          height: 280px;
        }

        .pie-chart-container {
          display: flex;
          justify-content: center;
        }

        .category-list {
          margin-top: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .category-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
        }

        .category-color {
          width: 12px;
          height: 12px;
          border-radius: 4px;
        }

        .category-name {
          flex: 1;
          color: #475569;
        }

        .category-value {
          font-weight: 600;
          color: #1e293b;
        }

        /* Two Column Grid */
        .two-column-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 28px;
        }

        .data-card {
          background: white;
          border-radius: 20px;
          padding: 20px;
          border: 1px solid #e8ecef;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .card-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .view-link {
          font-size: 13px;
          color: #10b981;
          text-decoration: none;
        }

        /* Orders List */
        .orders-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .order-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: #f8fafc;
          border-radius: 16px;
          transition: all 0.3s ease;
        }

        .order-item:hover {
          background: #f1f5f9;
          transform: translateX(4px);
        }

        .order-id {
          font-weight: 600;
          color: #10b981;
          font-family: monospace;
          font-size: 12px;
        }

        .order-product {
          font-size: 14px;
          font-weight: 500;
          color: #1e293b;
          margin: 4px 0;
        }

        .order-customer {
          font-size: 11px;
          color: #64748b;
        }

        .order-details {
          text-align: right;
        }

        .order-amount {
          font-weight: 700;
          color: #10b981;
          font-size: 15px;
        }

        .order-seller {
          font-size: 11px;
          color: #64748b;
          margin: 4px 0;
        }

        .order-status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }

        /* Top Sellers List */
        .top-sellers-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .seller-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .seller-rank {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
        }

        .seller-info {
          flex: 1;
        }

        .seller-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 14px;
          margin-bottom: 4px;
        }

        .seller-stats {
          display: flex;
          gap: 12px;
          font-size: 11px;
          color: #64748b;
        }

        .seller-growth {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          font-weight: 600;
        }

        .seller-growth.positive {
          color: #10b981;
        }

        /* Insights Card */
        .insights-card {
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          border-radius: 20px;
          padding: 24px;
        }

        .insights-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }

        .insights-header svg {
          color: #f59e0b;
        }

        .insights-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .insight-item {
          display: flex;
          gap: 14px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .insight-icon {
          font-size: 28px;
        }

        .insight-item h4 {
          font-size: 13px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px;
        }

        .insight-item p {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          line-height: 1.4;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 1024px) {
          .charts-row {
            grid-template-columns: 1fr;
          }
          .two-column-grid {
            grid-template-columns: 1fr;
          }
          .insights-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .partner-dashboard-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
          }
          .order-item {
            flex-direction: column;
            text-align: left;
            gap: 12px;
          }
          .order-details {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;